import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { base44 } from "../api/base44Client";
import {
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Container,
  Grid,
  AppBar,
  Toolbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Shield,
  Search,
  Plus,
  Eye,
  Download,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
} from "lucide-react";
import moment from "moment";

const STATUS_CONFIG = {
  bozza: { label: "Bozza", icon: Clock, color: "default" },
  incompleta: { label: "Incompleta", icon: AlertCircle, color: "warning" },
  completata: { label: "Completata", icon: CheckCircle2, color: "success" },
  inviata: { label: "Inviata", icon: Send, color: "primary" },
};

export default function AdminDashboard() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      const data = await base44.entities.FinancingRequest.list(
        "-updated_date",
        100,
      );
      setRequests(data);
      setLoading(false);
    }
    load();
  }, []);

  const filtered = requests.filter((r) => {
    if (filterStatus !== "all" && r.stato_pratica !== filterStatus)
      return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (r.pratica_id || "").toLowerCase().includes(s) ||
        (r.cognome || "").toLowerCase().includes(s) ||
        (r.nome || "").toLowerCase().includes(s) ||
        (r.codice_fiscale || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const stats = {
    totali: requests.length,
    bozza: requests.filter((r) => r.stato_pratica === "bozza").length,
    incompleta: requests.filter((r) => r.stato_pratica === "incompleta").length,
    completata: requests.filter((r) => r.stato_pratica === "completata").length,
    inviata: requests.filter((r) => r.stato_pratica === "inviata").length,
  };

  const handleExportAll = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pratiche-export-${moment().format("YYYY-MM-DD")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ bgcolor: "background.paper" }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{ py: 1, display: "flex", justifyContent: "space-between" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={18} color="white" />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Gestione Pratiche
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1 }}
                >
                  Pannello di controllo
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportAll}
                startIcon={<Download size={14} />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Esporta
              </Button>
              <Button
                component={RouterLink}
                to="/"
                variant="contained"
                size="small"
                startIcon={<Plus size={14} />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Nuova pratica
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: "Totali", value: stats.totali, color: "primary.main" },
            { label: "Bozza", value: stats.bozza, color: "text.secondary" },
            {
              label: "Incomplete",
              value: stats.incompleta,
              color: "warning.main",
            },
            {
              label: "Complete",
              value: stats.completata,
              color: "success.main",
            },
            { label: "Inviate", value: stats.inviata, color: "primary.main" },
          ].map((s) => (
            <Grid item xs={6} sm={2.4} key={s.label}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 3, textAlign: "center" }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, mt: 0.5, color: s.color }}
                >
                  {s.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cerca per ID, nome, cognome, CF..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 3, bgcolor: "background.paper" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: 3, bgcolor: "background.paper" }}
              >
                <MenuItem value="all">Tutti gli stati</MenuItem>
                <MenuItem value="bozza">Bozza</MenuItem>
                <MenuItem value="incompleta">Incompleta</MenuItem>
                <MenuItem value="completata">Completata</MenuItem>
                <MenuItem value="inviata">Inviata</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {filtered.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 8,
              textAlign: "center",
              borderRadius: 4,
              bgcolor: "action.hover",
            }}
          >
            <FileText
              size={48}
              style={{ color: "#9e9e9e", marginBottom: 16 }}
            />
            <Typography variant="body2" color="text.secondary">
              Nessuna pratica trovata
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filtered.map((req) => {
              const status =
                STATUS_CONFIG[req.stato_pratica] || STATUS_CONFIG.bozza;
              const StatusIcon = status.icon;
              return (
                <Paper
                  key={req.id}
                  variant="outlined"
                  component={RouterLink}
                  to={`/admin/pratica/${req.id}`}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    textDecoration: "none",
                    color: "inherit",
                    display: "block",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: 1,
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: `${status.color}.main`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <StatusIcon size={20} color="white" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700 }}
                            noWrap
                          >
                            {req.cognome && req.nome
                              ? `${req.cognome} ${req.nome}`
                              : "Dati non inseriti"}
                          </Typography>
                          <Chip
                            label={status.label}
                            size="small"
                            color={status.color}
                            sx={{
                              height: 18,
                              fontSize: "0.625rem",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              color: "text.secondary",
                            }}
                          >
                            {req.pratica_id}
                          </Typography>
                          {req.completion_percentage !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "primary.main" }}
                            >
                              {req.completion_percentage}%
                            </Typography>
                          )}
                          {req.campi_mancanti?.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: "warning.main" }}
                            >
                              {req.campi_mancanti.length} mancanti
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {req.updated_date
                          ? moment(req.updated_date).format("DD/MM/YY HH:mm")
                          : ""}
                      </Typography>
                      <Eye
                        size={14}
                        style={{ color: "#9e9e9e", marginTop: 4 }}
                      />
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}
